import { Controller, useForm } from "react-hook-form";
import { useCallback, useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";

import type { BaseItem, PatientProfile } from "@/core/types";
import { Button2 } from "@/components/UI2/atoms/Button";
import { captureErrorMessage, parseError } from "@/lib/helpers/captureErrorMessage";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import { PhoneNumberTextField } from "@/features/form/PhoneNumberTextField";
import { Select2 } from "@/components/UI2/molecules/Select";
import { showToast } from "@/providers/ToastProvider";
import { STATES } from "@/constants/states";
import { TextField2 } from "@/components/UI2/molecules/TextField";

import { basicInfoSchema, type BasicInfoFormValues } from "../schema";
import type { UpdatePatientProfileVariables } from "../api/queryFunctions/updatePatientProfile";
import { usePatientProfile } from "../api/queries/usePatientProfile";
import { useUpdatePatientProfile } from "../api/mutations/useUpdatePatientProfile";

export function BasicInfoForm() {
  const [isEditing, setIsEditing] = useState(false);

  const { data: profileData } = usePatientProfile();
  const { isPending, mutate } = useUpdatePatientProfile({
    onError: (error) => {
      captureErrorMessage(parseError({ error, fallback: "Failed to update patient profile" }), {
        tags: { component: "BasicInfoForm", errorType: "submit_profile" },
      });
      showToast([{ heading: "Failed to update profile.", variant: "error" }]);
    },
    onSuccess: () => {
      setIsEditing(false);
    },
  });

  const { control, formState, handleSubmit, reset } = useForm<BasicInfoFormValues>({
    defaultValues: {
      address: { city: "", state: "", street: "", zip_code: "" },
      dob: "",
      email: "",
      first_name: "",
      gender: "",
      last_name: "",
      mobile: "",
      preferred_name: "",
      preferred_pronouns: "",
    },
    mode: "onChange",
    resolver: zodResolver(basicInfoSchema),
  });

  useEffect(() => {
    if (profileData) reset(mapToFormValues(profileData));
  }, [profileData, reset]);

  const handleCancel = useCallback(() => {
    if (profileData) reset(mapToFormValues(profileData));
    setIsEditing(false);
  }, [profileData, reset]);

  const onSubmit = handleSubmit((values) => {
    if (!profileData) return;
    mutate({ ...profileData, ...values } as UpdatePatientProfileVariables);
  });

  return (
    <DashboardCard2
      Actions={
        <Button2 isDisabled={isEditing} onPress={() => setIsEditing(true)} variant="link">
          Edit
        </Button2>
      }
      title="Basic Information"
    >
      <form onSubmit={onSubmit}>
        <div className="tw:grid tw:gap-5 tw:md:grid-cols-2">
          <Controller
            control={control}
            name="first_name"
            render={({ field }) => (
              <TextField2
                hasStrongLabel
                isReadOnly
                label="Legal First Name"
                mode="input"
                onChange={field.onChange}
                value={field.value}
              />
            )}
          />

          <Controller
            control={control}
            name="last_name"
            render={({ field }) => (
              <TextField2
                hasStrongLabel
                isReadOnly
                label="Legal Last Name"
                mode="input"
                onChange={field.onChange}
                value={field.value}
              />
            )}
          />

          <Controller
            control={control}
            name="preferred_name"
            render={({ field, fieldState }) => (
              <TextField2
                description="What you'd like your provider to call you"
                errorMessage={fieldState.error?.message}
                hasStrongLabel
                isInvalid={!!fieldState.error}
                isReadOnly={!isEditing}
                label="Nickname"
                mode="input"
                onChange={field.onChange}
                value={field.value ?? ""}
              />
            )}
          />

          <Controller
            control={control}
            name="preferred_pronouns"
            render={({ field, fieldState }) =>
              isEditing ? (
                <Select2
                  errorMessage={fieldState.error?.message}
                  isInvalid={!!fieldState.error}
                  label="Preferred Pronouns"
                  onChange={(key) => field.onChange(key)}
                  options={PRONOUNS_OPTIONS}
                  placeholder="Select..."
                  value={field.value ?? ""}
                />
              ) : (
                <TextField2
                  hasStrongLabel
                  isReadOnly
                  label="Preferred Pronouns"
                  mode="input"
                  onChange={field.onChange}
                  value={field.value ?? ""}
                />
              )
            }
          />

          <Controller
            control={control}
            name="dob"
            render={({ field }) => (
              <TextField2
                hasStrongLabel
                isReadOnly
                label="Date of Birth"
                mode="input"
                onChange={field.onChange}
                value={field.value}
              />
            )}
          />

          <Controller
            control={control}
            name="gender"
            render={({ field, fieldState }) =>
              isEditing ? (
                <Select2
                  errorMessage={fieldState.error?.message}
                  isInvalid={!!fieldState.error}
                  label="Gender"
                  onChange={(key) => field.onChange(key)}
                  options={GENDER_OPTIONS}
                  placeholder="Select..."
                  value={field.value ?? ""}
                />
              ) : (
                <TextField2
                  hasStrongLabel
                  isReadOnly
                  label="Gender"
                  mode="input"
                  onChange={field.onChange}
                  value={getDisplayName(GENDER_OPTIONS, field.value ?? "")}
                />
              )
            }
          />

          <Controller
            control={control}
            name="mobile"
            render={({ field, fieldState }) => (
              <PhoneNumberTextField
                errorMessage={fieldState.error?.message}
                hasStrongLabel
                isInvalid={!!fieldState.error}
                isReadOnly={!isEditing}
                isRequired={isEditing}
                label="Mobile"
                phoneNumberInputProps={{ onChange: field.onChange, value: field.value }}
              />
            )}
          />

          <Controller
            control={control}
            name="email"
            render={({ field }) => (
              <TextField2
                hasStrongLabel
                isReadOnly
                label="Email"
                mode="input"
                onChange={field.onChange}
                value={field.value}
              />
            )}
          />

          <Controller
            control={control}
            name="address.street"
            render={({ field, fieldState }) => (
              <TextField2
                errorMessage={fieldState.error?.message}
                hasStrongLabel
                isInvalid={!!fieldState.error}
                isReadOnly={!isEditing}
                label="Street"
                mode="input"
                onChange={field.onChange}
                value={field.value}
              />
            )}
          />

          <Controller
            control={control}
            name="address.city"
            render={({ field, fieldState }) => (
              <TextField2
                errorMessage={fieldState.error?.message}
                hasStrongLabel
                isInvalid={!!fieldState.error}
                isReadOnly={!isEditing}
                label="City"
                mode="input"
                onChange={field.onChange}
                value={field.value}
              />
            )}
          />

          <Controller
            control={control}
            name="address.state"
            render={({ field, fieldState }) =>
              isEditing ? (
                <Select2
                  errorMessage={fieldState.error?.message}
                  hasStrongLabel
                  isInvalid={!!fieldState.error}
                  label="State"
                  onChange={(key) => field.onChange(key)}
                  options={STATE_OPTIONS}
                  placeholder="Select..."
                  value={field.value ?? ""}
                />
              ) : (
                <TextField2
                  hasStrongLabel
                  isReadOnly
                  label="State"
                  mode="input"
                  onChange={field.onChange}
                  value={field.value ?? ""}
                />
              )
            }
          />

          <Controller
            control={control}
            name="address.zip_code"
            render={({ field, fieldState }) => (
              <TextField2
                errorMessage={fieldState.error?.message}
                hasStrongLabel
                isInvalid={!!fieldState.error}
                isReadOnly={!isEditing}
                label="Zip"
                mode="input"
                onChange={field.onChange}
                value={field.value}
              />
            )}
          />
        </div>

        {isEditing && (
          <div className="tw:mt-5 tw:flex tw:justify-end tw:gap-3">
            <Button2 onPress={handleCancel} size="md" type="button" variant="secondary">
              Cancel
            </Button2>

            <Button2
              isDisabled={!formState.isValid || isPending}
              isLoading={isPending}
              labelLoading={null}
              size="md"
              type="submit"
              variant="primary"
            >
              Save
            </Button2>
          </div>
        )}
      </form>
    </DashboardCard2>
  );
}

function getDisplayName(options: BaseItem[], id: string): string {
  return options.find((o) => o.id === id)?.name ?? id;
}

function mapToFormValues(data: PatientProfile): BasicInfoFormValues {
  return {
    address: {
      city: data.address.city ?? "",
      state: data.address.state ?? "",
      street: data.address.street ?? "",
      zip_code: data.address.zip_code ?? "",
    },
    dob: data.dob ?? "",
    email: data.email ?? "",
    first_name: data.first_name ?? "",
    gender: data.gender ?? "",
    last_name: data.last_name ?? "",
    mobile: data.mobile ?? "",
    preferred_name: data.preferred_name ?? "",
    preferred_pronouns: data.preferred_pronouns ?? "",
  };
}

const GENDER_OPTIONS = [
  { id: "male", name: "Male" },
  { id: "female", name: "Female" },
] as const satisfies BaseItem[];

const PRONOUNS_OPTIONS = [
  { id: "He / him", name: "He / him" },
  { id: "She / her", name: "She / her" },
  { id: "They / them", name: "They / them" },
] as const satisfies BaseItem[];

const STATE_OPTIONS: BaseItem[] = STATES.map((s) => ({ id: s.value, name: s.display }));
